import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import Loading from "../../../components/common/Loading";
import {
  useAttractions,
  type Attraction,
} from "../../../services/attractionService";

const Attractions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch attractions from API
  const {
    data: attractionsResponse,
    isLoading,
    error,
  } = useAttractions({
    locale: currentLanguage,
    per_page: 100, // Get all attractions to group by category
  });

  // Group attractions by category
  const groupedAttractions = useMemo(() => {
    const attractions = attractionsResponse?.attractions || [];
    const grouped: Record<string, Attraction[]> = {};

    attractions.forEach((attraction) => {
      const category = attraction.category || "Other";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(attraction);
    });

    return Object.entries(grouped).map(([category, items]) => ({
      category,
      items,
    }));
  }, [attractionsResponse?.attractions]);

  // Trigger animations when data loads
  useEffect(() => {
    if (!isLoading) {
      setIsDataLoaded(true);
    }
  }, [isLoading]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      <main className="relative">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] md:min-h-[70vh] lg:min-h-[80vh] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%), url("https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1200&h=800&fit=crop")`,
            }}
          />
          <div className="absolute inset-0 bg-black/20" />

          <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight tracking-tight">
                Discover Djichami's Heritage
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light leading-relaxed max-w-3xl mx-auto">
                Explore the rich history and natural wonders of Djichami, a
                community with a unique cultural identity.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 min-w-[200px]"
              >
                Explore Attractions
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Attractions Sections */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loading />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 dark:text-red-400">
                Error loading attractions. Please try again later.
              </p>
            </div>
          ) : groupedAttractions.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 dark:text-gray-400">
                No attractions found.
              </p>
            </div>
          ) : (
            groupedAttractions.map((section, sectionIndex) => (
              <motion.section
                key={section.category}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.8, delay: sectionIndex * 0.2 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                    {section.category}
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-stone-600 mx-auto rounded-full"></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {section.items.map((attraction, index) => (
                    <motion.div
                      key={attraction.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={
                        isDataLoaded
                          ? { opacity: 1, y: 0 }
                          : { opacity: 0, y: 30 }
                      }
                      viewport={{ once: true, amount: 0.1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/${currentLanguage}/attractions/${attraction.id}`
                        )
                      }
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={
                            attraction.image ||
                            "https://via.placeholder.com/400x300?text=No+Image"
                          }
                          alt={attraction.title || "Attraction"}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                          <h3 className="text-white text-lg font-bold leading-tight">
                            {attraction.title || "Untitled Attraction"}
                          </h3>
                        </div>
                      </div>

                      <div className="p-6 space-y-3">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                          {attraction.title || "Untitled Attraction"}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm sm:text-base line-clamp-3">
                          {attraction.description || ""}
                        </p>
                        {attraction.location && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            📍 {attraction.location}
                          </p>
                        )}
                        <div className="pt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/${currentLanguage}/attractions/${attraction.id}`
                              );
                            }}
                            className="inline-flex items-center text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors duration-200"
                          >
                            {t("attractions.learnMore", "Learn More")}
                            <svg
                              className="w-4 h-4 ml-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            ))
          )}
        </div>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-emerald-600 via-stone-600 to-emerald-700 text-white py-16"
        >
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Experience Djichami's Beauty
            </h2>
            <p className="text-lg sm:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Immerse yourself in the rich cultural heritage and natural wonders
              that make Djichami truly special.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Plan Your Visit
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-emerald-600 transition-all duration-300"
              >
                View on Map
              </motion.button>
            </div>
          </div>
        </motion.section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Attractions;
